import { type ColDef, type GridApi, type GridReadyEvent, type ICellRendererParams, type RowClassParams, type ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ExternalLink, Printer, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link, useSearchParams } from "react-router";
import { apiPost, apiSend, useApi } from "../api";
import type { PortalConfig } from "../config";
import { confirmBox } from "../confirm";
import { messagePreview } from "../emailText";
import { date, dateTime, money, trackingUrl } from "../format";
import type { LeadList, LeadRow, LeadTab, LeadTag, Lookups } from "../types";
import { CustomerCard } from "./ticket/TopRow";
import { MessagesCard } from "./ticket/RightColumn";
import { TouchesCard } from "./ticket/MiddleColumn";
import { TicketContext, useTicket, useTicketContext } from "./ticket/useTicket";
import { theme } from "./TicketsPage";

/**
 * Leads (owner, 2026-11-04): tickets whose watch hasn't arrived. To do (everything waiting on staff, tagged with
 * why), Follow up (label sent, no package yet), Mailers (mailed labels and boxes), All leads. The selected lead
 * opens on the right: its next step, then Touches and Messages side by side.
 */
const TABS: { id: LeadTab; label: string; hint: string }[] = [
  { id: "todo", label: "To do", hint: "Everything waiting on staff, oldest first: replies (including In Transit and Lead Archived tickets with unread messages), addresses to verify, insurance over the label limit, labels that couldn't be bought, and mailers to send." },
  { id: "follow", label: "Follow up", hint: "Customers with a label who haven't mailed their watch yet. Leads with no package 90 days after the label move to Lead Archived on their own." },
  { id: "mailers", label: "Mailers", hint: "Labels and boxes already mailed: on their way (Mailer Outgoing) or delivered and waiting for the watch (Mailer Arrived)." },
  { id: "all", label: "All leads", hint: "Every lead whose watch hasn't arrived yet." },
];

const TAG_LABEL: Record<LeadTag, string> = {
  reply: "Reply", address: "Verify address", insurance: "Insurance", "no-label": "No label yet", "mail-label": "Mail label", "mail-box": "Mail box",
};

const name = (r: LeadRow) => [r.firstName, r.lastName].filter(Boolean).join(" ") || "(no name)";
const tagText = (r: LeadRow) => r.tags.map((t) => TAG_LABEL[t]).join(", ");
const where = (r: LeadRow) => r.statusKey === "mailer_arrived" ? "Delivered" : r.statusKey === "mailer_outgoing" ? "On its way" : r.status;
const carrierFor = (n: string) => (/^1Z/i.test(n) ? "UPS" : "USPS");

function TagsCell(p: ICellRendererParams<LeadRow>) {
  const r = p.data;
  if (!r) return null;
  if (!r.tags.length) return <span className="lead-pill plain">{r.status}</span>;
  return <span className="lead-tags">{r.tags.map((t) => <span key={t} className={`lead-pill ${t}`}>{TAG_LABEL[t]}</span>)}</span>;
}

function MessageCell(p: ICellRendererParams<LeadRow & { preview: string | null }>) {
  const r = p.data;
  if (!r?.preview) return <span className="muted">—</span>;
  return (
    <span className={`lead-msg${r.unread ? " unread" : ""}`}>
      <span className="text">{r.preview}</span>
      <span className="when">{r.lastMessageVia === "text" ? "Text" : "Email"} · {dateTime(r.lastMessageAt)}</span>
    </span>
  );
}

function TicketCell(p: ICellRendererParams<LeadRow>) {
  const r = p.data;
  if (!r) return null;
  return <span className="lead-no">{r.ticketNo}{r.unread && <span className="dot" title="Unread customer message" />}</span>;
}

export function LeadsPage({ config, lookups }: { config: PortalConfig; lookups: Lookups | null }) {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.id === params.get("tab"))?.id ?? "todo") as LeadTab;
  const [reload, setReload] = useState(0);
  const { data, error, loading } = useApi<LeadList>(config, "/lead-queue", reload);
  const [quick, setQuick] = useState("");
  const [selectedNo, setSelectedNo] = useState<number | null>(null);
  const [checked, setChecked] = useState<LeadRow[]>([]);
  const gridRef = useRef<GridApi<LeadRow> | null>(null);

  const rows = useMemo(() => (data?.rows ?? []).filter((r) => r.tabs.includes(tab))
    .map((r) => ({ ...r, preview: messagePreview(r.lastMessage), needs: tagText(r), name: name(r), where: where(r) })), [data, tab]);
  const selected = rows.find((r) => r.ticketNo === selectedNo) ?? data?.rows.find((r) => r.ticketNo === selectedNo) ?? null;
  // Pick the first lead when the tab changes or nothing is selected.
  useEffect(() => { if (rows.length && !rows.some((r) => r.ticketNo === selectedNo)) setSelectedNo(rows[0]!.ticketNo); }, [rows, selectedNo]);

  const columns = useMemo<ColDef<LeadRow>[]>(() => {
    const mailers = tab === "mailers";
    return [
      { field: "ticketNo", headerName: "Ticket", width: 105, cellRenderer: TicketCell, filter: "agNumberColumnFilter" },
      { colId: "name", headerName: "Customer", width: 160, valueGetter: (p) => p.data && name(p.data) },
      mailers
        ? { colId: "where", headerName: "Where it is", width: 130, valueGetter: (p) => p.data && where(p.data) }
        : { colId: "needs", headerName: tab === "todo" ? "Needs" : "Status", width: 190, cellRenderer: TagsCell,
            valueGetter: (p) => p.data && (p.data.tags.length ? tagText(p.data) : p.data.status) },
      ...(mailers ? [
        { colId: "mailing", headerName: "Mailing", width: 115, valueGetter: (p) => p.data?.mailer ? (p.data.mailer.kind === "box" ? "Box + label" : "Label") : "" },
        { colId: "sent", headerName: "Sent", width: 120, filter: false, valueGetter: (p) => p.data?.mailer?.sentAt ?? "",
          valueFormatter: (p: ValueFormatterParams<LeadRow, string>) => date(p.value) },
        { colId: "tracking", headerName: "Tracking", width: 190, valueGetter: (p) => p.data?.mailer?.trackingNumber ?? "",
          cellRenderer: (p: ICellRendererParams<LeadRow, string>) => {
            const u = p.value ? trackingUrl(carrierFor(p.value), p.value) : null;
            return u ? <a href={u} target="_blank" rel="noreferrer">{p.value}</a> : (p.value || "—");
          } },
      ] as ColDef<LeadRow>[] : []),
      { field: "brands", headerName: "Brands", width: 140 },
      { field: "insured", headerName: "Insured", width: 105, filter: "agNumberColumnFilter", type: "rightAligned",
        valueFormatter: (p: ValueFormatterParams<LeadRow, number>) => (p.value ? money(p.value) : "—") },
      { field: "days", headerName: "Days", width: 90, filter: "agNumberColumnFilter", type: "rightAligned", sort: "desc",
        headerTooltip: "Days since the label (or since the ticket was made, if there is no label)",
        cellClass: (p) => (Number(p.value) >= 30 ? "days-late" : Number(p.value) >= 7 ? "days-warn" : "") },
      { colId: "lastTouch", headerName: "Last touch", width: 175, valueGetter: (p) => p.data?.lastTouch ? `${p.data.lastTouch} · ${date(p.data.lastTouchAt)}` : "" },
      { field: "touches", headerName: "Touches", width: 100, filter: "agNumberColumnFilter", type: "rightAligned" },
      { colId: "message", headerName: "Latest customer message", flex: 1, minWidth: 260, cellRenderer: MessageCell,
        valueGetter: (p) => (p.data as LeadRow & { preview?: string | null } | undefined)?.preview ?? "",
        tooltipValueGetter: (p) => (p.data as LeadRow & { preview?: string | null } | undefined)?.preview ?? undefined },
      { field: "email", headerName: "Email", width: 200, hide: true },
      { field: "phone", headerName: "Phone", width: 130, hide: true },
      { colId: "place", headerName: "City", width: 150, hide: true, valueGetter: (p) => [p.data?.city, p.data?.state].filter(Boolean).join(", ") },
    ];
  }, [tab]);

  const defaultColDef = useMemo<ColDef<LeadRow>>(() => ({ sortable: true, filter: "agTextColumnFilter", resizable: true }), []);
  const onGridReady = useCallback((e: GridReadyEvent<LeadRow>) => { gridRef.current = e.api; }, []);
  useEffect(() => { gridRef.current?.setGridOption("quickFilterText", quick); }, [quick]);
  const rowClass = useCallback((p: RowClassParams<LeadRow>) => (p.data?.ticketNo === selectedNo ? "lead-current" : ""), [selectedNo]);
  useEffect(() => { gridRef.current?.redrawRows(); }, [selectedNo]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div className="grid-page leads-page">
      <div className="leads-head">
        <h2>Leads</h2>
        <nav className="lead-tabs" aria-label="Lead lists">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={t.id === tab ? "on" : ""} aria-pressed={t.id === tab}
              onClick={() => { const n = new URLSearchParams(params); n.set("tab", t.id); setParams(n); setChecked([]); }}>
              {t.label} <span className={`count${t.id === "todo" && (data?.counts.todo ?? 0) > 0 ? " hot" : ""}`}>{data ? data.counts[t.id].toLocaleString() : "…"}</span>
            </button>
          ))}
        </nav>
        <input type="search" value={quick} onChange={(e) => setQuick(e.target.value)} placeholder="Filter: name, email, phone, ticket #, brand…" aria-label="Filter leads" />
        <button type="button" className="tb-btn" onClick={refresh} title="Refresh the list"><RefreshCw size={15} /> {loading ? "Updating…" : "Refresh"}</button>
      </div>
      <p className="hint lead-hint">{TABS.find((t) => t.id === tab)!.hint}</p>
      {error && <p className="error">{error.message}</p>}

      {checked.length > 0 && <LeadBulk config={config} lookups={lookups} rows={checked} onDone={() => { refresh(); gridRef.current?.deselectAll(); }}
        onClear={() => gridRef.current?.deselectAll()} />}

      <div className="leads-split">
        <div className="grid-box leads-grid">
          <AgGridReact<LeadRow>
            theme={theme}
            rowData={data ? rows : null}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowId={(p) => p.data.ticketId}
            rowSelection={{ mode: "multiRow", checkboxes: true, headerCheckbox: true, enableClickSelection: false, selectAll: "filtered" }}
            onSelectionChanged={(e) => setChecked(e.api.getSelectedRows())}
            onRowClicked={(e) => e.data && setSelectedNo(e.data.ticketNo)}
            getRowClass={rowClass}
            onGridReady={onGridReady}
            pagination
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100, 250]}
            tooltipShowDelay={400}
            overlayNoRowsTemplate="Nothing here right now."
            loading={!data && !error}
          />
        </div>
        <aside className="lead-ws" aria-label="Selected lead">
          {selected ? <LeadWorkspace key={selected.ticketNo} config={config} lookups={lookups} row={selected} onChanged={refresh} />
            : <p className="muted">Pick a lead from the list.</p>}
        </aside>
      </div>
    </div>
  );
}

/** Selected leads: status, read/unread, resend the label link, archive. */
function LeadBulk({ config, lookups, rows, onDone, onClear }: { config: PortalConfig; lookups: Lookups | null; rows: LeadRow[]; onDone: () => void; onClear: () => void }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [statusId, setStatusId] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const ticketNos = rows.map((r) => r.ticketNo);
  const n = rows.length;
  const s = (k: number) => `${k} lead${k === 1 ? "" : "s"}`;
  const run = async (fn: () => Promise<string>) => {
    setBusy(true); setNote(null);
    try { setNote({ ok: true, text: await fn() }); onDone(); } catch (e) { setNote({ ok: false, text: (e as Error).message }); } finally { setBusy(false); }
  };
  const archivedId = lookups?.statuses.find((x) => x.systemKey === "lead_archived")?.id;
  const statuses = lookups?.statuses.filter((x) => x.isActive) ?? [];
  return (
    <div className="bulk-bar lead-bulk" role="region" aria-label="Selected leads">
      <strong>{s(n)} selected</strong>
      <select value={statusId} onChange={(e) => setStatusId(e.target.value)} aria-label="New status">
        <option value="">Change status…</option>
        {statuses.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
      <button type="button" disabled={!statusId || busy} onClick={async () => {
        const st = statuses.find((x) => String(x.id) === statusId);
        if (!st || !(await confirmBox(`Change ${s(n)} to "${st.name}"?\n\nNo email or text is sent.`, { title: "Change status", ok: "Change status" }))) return;
        void run(async () => { const r = await apiPost<{ changed: number }>(config, token, "/tickets/actions/status", { ticketNos, statusId: st.id }); setStatusId(""); return `${s(r.changed)} moved to ${st.name}.`; });
      }}>Apply</button>
      <button type="button" disabled={busy} onClick={() => void run(async () => { await apiPost(config, token, "/tickets/actions/mark-read", { ticketNos }); return `Marked ${s(n)} read.`; })}>Mark read</button>
      <button type="button" disabled={busy} onClick={async () => {
        if (!(await confirmBox(`Email (and text, where allowed) the label link to ${s(n)}?`, { title: "Resend label link", ok: "Resend" }))) return;
        void run(async () => {
          let ok = 0; const failed: number[] = [];
          for (const no of ticketNos) {
            try { await apiSend(config, token, "POST", `/tickets/${no}/resend-label`, {}); ok++; } catch { failed.push(no); }
          }
          return `Label link resent to ${s(ok)}.${failed.length ? ` Not sent: #${failed.join(", #")}.` : ""}`;
        });
      }}>Resend label link</button>
      <button type="button" className="danger" disabled={busy || !archivedId} onClick={async () => {
        if (!(await confirmBox(`Move ${s(n)} to Lead Archived?`, { title: "Archive", ok: "Archive", danger: true }))) return;
        void run(async () => { const r = await apiPost<{ changed: number }>(config, token, "/tickets/actions/status", { ticketNos, statusId: archivedId }); return `${s(r.changed)} archived.`; });
      }}>Archive</button>
      <button type="button" className="link" onClick={onClear}>Clear selection</button>
      {note && <span className={note.ok ? "ok" : "error"}>{note.text}</span>}
    </div>
  );
}

/** The selected lead: what to do next, the customer, then Touches and Messages side by side. */
function LeadWorkspace({ config, lookups, row, onChanged }: { config: PortalConfig; lookups: Lookups | null; row: LeadRow; onChanged: () => void }) {
  const { detail, loadError, problem, setProblem, notice, setNotice, saving, save, reload } = useTicket(config, String(row.ticketNo));
  // Any change made here refreshes the list (tags, counts) once the ticket has been saved.
  const first = useRef(true);
  useEffect(() => { if (!detail) return; if (first.current) { first.current = false; return; } onChanged(); }, [detail, onChanged]);
  if (loadError) return <p className="error">{loadError}</p>;
  if (!detail) return <p className="muted">Loading #{row.ticketNo}…</p>;
  return (
    <TicketContext.Provider value={{ config, lookups, detail, save, saving, reload, notify: setNotice }}>
      <div className="tp lead-tp">
        <div className="lead-ws-head">
          <h3>#{row.ticketNo} · {name(row)}</h3>
          <span className="lead-pill plain">{detail.ticket.status}</span>
          <span className="spacer" />
          <Link className="btn outline" to={`/tickets/${row.ticketNo}`}><ExternalLink size={15} />Open ticket</Link>
          <ArchiveButton />
        </div>
        {problem && <div className="problem" role="alert"><span>{problem.text}</span><button type="button" className="link-btn" onClick={() => setProblem(null)}>Dismiss</button></div>}
        {notice && <div className="notice" role="status"><span>{notice}</span><button type="button" className="link-btn" onClick={() => setNotice(null)}>Dismiss</button></div>}
        <NextStep row={row} />
        <CustomerCard />
        <div className="tp-pair"><TouchesCard /><MessagesCard /></div>
      </div>
    </TicketContext.Provider>
  );
}

function ArchiveButton() {
  const { detail, lookups, save, saving } = useTicketContext();
  const id = lookups?.statuses.find((s) => s.systemKey === "lead_archived")?.id;
  if (!id || detail.ticket.statusId === id) return null;
  return <button type="button" className="btn outline danger-text" disabled={saving} onClick={async () => {
    if (await confirmBox(`Move #${detail.ticket.ticketNo} to Lead Archived? It comes back to To do if the customer writes.`, { title: "Archive lead", ok: "Archive", danger: true }))
      await save("PATCH", "", { statusId: id, expectedUpdatedAt: detail.ticket.updatedAt });
  }}>Archive</button>;
}

/** What the lead needs next, by its tags and status. */
function NextStep({ row }: { row: LeadRow }) {
  const { detail, save, saving } = useTicketContext();
  const intake = detail.labels.filter((l) => l.toShop && !l.voidedAt).sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
  const labelFailed = detail.messages.find((m) => m.isSystem && /^Label 1 could not be bought/.test(m.body))?.body.replace(/^Label 1 could not be bought:\s*/, "");
  const tags = row.tags;
  const mailer = row.mailer;
  const blocks: React.ReactNode[] = [];

  if (tags.includes("reply")) blocks.push(
    <div key="reply" className="ns-block">
      <p><strong>New message from the customer.</strong> Reply in Touches (text) or Messages (email). It leaves To do once marked read.</p>
      <div className="btn-row wrap"><button type="button" className="btn primary" disabled={saving} onClick={() => void save("POST", "/mark-read")}>Mark read</button>
        {intake && (row.statusKey === "label_sent" || row.statusKey === "label_sent_old") && <ResendLabel />}</div>
    </div>);

  if (tags.includes("address") || tags.includes("no-label")) blocks.push(
    <div key="label" className="ns-block">
      <p><strong>{tags.includes("address") ? "Check the address." : "No label yet."}</strong>{" "}
        {labelFailed ? <>The label couldn't be bought: {labelFailed}. </> : null}
        Fix the address under Customer Information if needed (it's checked with Shippo), then buy and send the label.</p>
      <div className="btn-row wrap">
        <button type="button" className="btn green" disabled={saving} onClick={() => void save("POST", "/send-label")}>{saving ? "Working…" : "Buy label & send"}</button>
      </div>
    </div>);

  if (tags.includes("insurance")) blocks.push(<InsuranceStep key="ins" />);

  if (tags.includes("mail-label") || tags.includes("mail-box") || row.statusKey === "mailer_outgoing" || row.statusKey === "mailer_arrived") {
    blocks.push(<MailerStep key="mailer" row={row} hasLabel={!!intake} />);
  }

  if (!blocks.length && (row.statusKey === "label_sent" || row.statusKey === "label_sent_old")) blocks.push(
    <div key="follow" className="ns-block">
      <p><strong>Label sent {row.days} day{row.days === 1 ? "" : "s"} ago; no package scan yet.</strong>{row.lastTouchAt ? ` Last touch: ${row.lastTouch} on ${date(row.lastTouchAt)}.` : " No touches yet."}
        {row.days < 90 ? ` Moves to Lead Archived in ${90 - row.days} days if nothing arrives.` : ""}</p>
      <div className="btn-row wrap">
        <ResendLabel />
        <MailerStart />
      </div>
    </div>);

  if (!blocks.length) blocks.push(<p key="none" className="hint">Nothing to do here right now.</p>);

  return (
    <section className="next-step" aria-label="Next step">
      <div className="ns-head"><span className="ns-title">Next step</span>
        {tags.map((g) => <span key={g} className={`lead-pill ${g}`}>{TAG_LABEL[g]}</span>)}
        <span className="spacer" />
        {intake?.labelUrl && <a className="link-btn" href={intake.labelUrl} target="_blank" rel="noreferrer">View label</a>}
        {intake?.trackingNumber && trackingUrl(intake.carrier, intake.trackingNumber) && <a className="link-btn" href={trackingUrl(intake.carrier, intake.trackingNumber)!} target="_blank" rel="noreferrer">Track package</a>}
      </div>
      {blocks}
    </section>
  );
}

function ResendLabel() {
  const { save, saving } = useTicketContext();
  return <button type="button" className="btn outline" disabled={saving} onClick={async () => {
    if (await confirmBox("Email the label link again, and text it if the customer allows texts?", { title: "Resend label link", ok: "Resend" })) await save("POST", "/resend-label");
  }}>Resend label link</button>;
}

function MailerStart() {
  const { save, saving } = useTicketContext();
  return <>
    <button type="button" className="btn outline" disabled={saving} onClick={() => void save("POST", "/mailer", { kind: "label" })}>Mail a label instead</button>
    <button type="button" className="btn outline" disabled={saving} onClick={() => void save("POST", "/mailer", { kind: "box" })}>Mail a box</button>
  </>;
}

function InsuranceStep() {
  const { detail, lookups, save, saving } = useTicketContext();
  const t = detail.ticket;
  const [value, setValue] = useState(t.insuranceAmount ? String(Number(t.insuranceAmount)) : "");
  return (
    <div className="ns-block">
      <p><strong>Shipping protection of {money(t.insuranceAmount ?? 0)} is over the {money(2500)} limit for a label,</strong> so no label was made.
        Lower the declared value with the customer and send a label, or mail them a box.</p>
      <div className="btn-row wrap ns-inline">
        <label>Declared value <input type="number" min={0} step={1} value={value} onChange={(e) => setValue(e.target.value)} /></label>
        <button type="button" className="btn outline" disabled={saving || value === String(Number(t.insuranceAmount ?? 0))}
          onClick={() => void save("PATCH", "", { insuranceAmount: value === "" ? null : value, expectedUpdatedAt: t.updatedAt })}>Save</button>
        <button type="button" className="btn green" disabled={saving || Number(t.insuranceAmount ?? 0) > 2500}
          title={Number(t.insuranceAmount ?? 0) > 2500 ? "Lower the declared value to $2,500 or less first" : undefined}
          onClick={() => void save("POST", "/send-label")}>Buy label & send</button>
        <button type="button" className="btn outline" disabled={saving} onClick={() => void save("POST", "/mailer", { kind: "box" })}>Mail a box</button>
      </div>
      {lookups && <p className="hint">The first {money(lookups.pricing.freeInsuranceAmount)} is free; above that {Math.round(lookups.pricing.insuranceRate * 100)}% is added to the estimate.</p>}
    </div>
  );
}

/** Requested → Printed → Mailed → On its way → Delivered. */
function MailerStep({ row, hasLabel }: { row: LeadRow; hasLabel: boolean }) {
  const { config, detail, save, saving, reload } = useTicketContext();
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [tracking, setTracking] = useState("");
  const [printed, setPrinted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const m = row.mailer;
  const box = m ? m.kind === "box" : row.statusKey === "mail_box";
  const stage = !m ? 0 : m.state === "requested" ? (printed ? 2 : 1) : m.state === "sent" ? 3 : 5;
  const steps = [
    { label: "Requested", sub: m ? `${date(m.requestedAt)} · ${m.byCustomer ? "customer" : "staff"}` : "" },
    { label: "Printed", sub: printed ? "Label & slip opened" : "" },
    { label: "Mailed", sub: m?.sentAt ? date(m.sentAt) : "" },
    { label: "On its way", sub: m?.trackingNumber ?? "" },
    { label: "Delivered", sub: m?.deliveredAt ? date(m.deliveredAt) : "" },
  ];
  const act = async (path: string, body: unknown = {}) => {
    setBusy(true); setErr(null);
    try { await apiSend(config, token, "POST", path, body); reload(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const track = m?.trackingNumber ? trackingUrl(carrierFor(m.trackingNumber), m.trackingNumber) : null;
  return (
    <div className="ns-block">
      <p><strong>{!m ? `Mail a ${box ? "box with a label" : "printed label"}.`
        : m.state === "requested" ? `Mail a ${m.kind === "box" ? "box with a label" : "printed label"} (asked for ${date(m.requestedAt)} by the ${m.byCustomer ? "customer" : "staff"}).`
        : m.state === "sent" ? `${m.kind === "box" ? "Box" : "Label"} mailed ${date(m.sentAt)}. Mark it delivered when tracking shows it arrived.`
        : `${m.kind === "box" ? "Box" : "Label"} delivered ${date(m.deliveredAt)}. Waiting for the customer to send the watch in.`}</strong></p>
      <ol className="mailer-steps">
        {steps.map((s, i) => (
          <li key={s.label} className={i < stage ? "done" : i === stage ? "now" : ""}><span className="bar" /><span className="lbl">{s.label}</span><span className="sub">{s.sub}</span></li>
        ))}
      </ol>
      {!m && <div className="btn-row wrap"><button type="button" className="btn primary" disabled={saving} onClick={() => void save("POST", "/mailer", { kind: box ? "box" : "label" })}>Start mailer</button></div>}
      {m?.state === "requested" && (
        <div className="btn-row wrap ns-inline">
          {hasLabel
            ? <a className="btn outline" href={`${config.apiUrl}/c/${detail.ticket.id}/label`} target="_blank" rel="noreferrer" onClick={() => setPrinted(true)}><Printer size={15} />Print label & slip</a>
            : <button type="button" className="btn outline" disabled={saving} onClick={() => void save("POST", "/send-label")}>Buy label first</button>}
          <label>Outgoing tracking # <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="optional" /></label>
          <button type="button" className="btn green" disabled={busy || !hasLabel} onClick={() => void act(`/mailers/${m.id}/sent`, { trackingNumber: tracking })}>Mark sent</button>
          <button type="button" className="link-btn danger-text" disabled={busy} onClick={async () => {
            if (await confirmBox("Cancel this mailer?", { ok: "Cancel mailer", cancel: "Keep it", danger: true })) await act(`/mailers/${m.id}/cancel`);
          }}>Cancel mailer</button>
        </div>
      )}
      {m?.state === "sent" && (
        <div className="btn-row wrap">
          {track && <a className="btn outline" href={track} target="_blank" rel="noreferrer">Track package</a>}
          <button type="button" className="btn green" disabled={busy} onClick={() => void act(`/mailers/${m.id}/delivered`)}>Mark delivered</button>
        </div>
      )}
      {m?.state === "delivered" && <div className="btn-row wrap"><ResendLabel /></div>}
      {err && <p className="warn">{err}</p>}
    </div>
  );
}
